import { useMemo } from 'react';
import { plural, t } from '@lingui/macro';

import ReviewStarFill from '@rover/icons/svg/review-star-fill.svg';
import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import type { ThemeSpacing } from '@rover/kibble/styles/themeTypes';
import useTheme from '@rover/kibble/themes/useTheme';
import { useApiPersonDetailReviewsList } from '@rover/rsdk/src/apiClient/latest/api-person-detail-reviews-list';

import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { getInitialReviewsParams } from '../../utils/utils';

export type DistributionVariant = 'page' | 'gallerySm' | 'galleryLg' | 'review';

interface BarConfig {
  listGap: ThemeSpacing;
  listMarginTop: ThemeSpacing;
  listPaddingRight: ThemeSpacing | undefined;
  gap: ThemeSpacing;
  borderRadius: string | undefined;
  cornerRadiusRight: string | undefined;
  trackBackground: (theme: ReturnType<typeof useTheme>) => string;
  labelFontWeight: 'regular' | 'semibold';
  barsDisplay?: string | string[];
}

interface VariantConfig {
  headingSize: '400' | '500' | '600';
  starIconSize: number;
  ratingsTextSize: '200' | '300';
  contentPaddingLeft: ThemeSpacing | undefined;
  containerProps: (theme: ReturnType<typeof useTheme>) => Record<string, unknown>;
  showWhileLoading: boolean;
  bar: BarConfig;
}

const galleryBar: BarConfig = {
  listGap: '1x',
  listMarginTop: '3x',
  listPaddingRight: '4x',
  gap: '3x',
  borderRadius: '8px',
  cornerRadiusRight: undefined,
  trackBackground: (theme) => theme.colors.border.primary,
  labelFontWeight: 'semibold',
  barsDisplay: ['none', 'flex', 'flex', 'flex'],
};

const galleryContainerProps = (): Record<string, unknown> => ({
  p: '4x',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const galleryBase: VariantConfig = {
  headingSize: '400',
  starIconSize: 24,
  ratingsTextSize: '200',
  contentPaddingLeft: '5x',
  containerProps: galleryContainerProps,
  showWhileLoading: true,
  bar: galleryBar,
};

const pageConfig: VariantConfig = {
  headingSize: '600',
  starIconSize: 32,
  ratingsTextSize: '200',
  contentPaddingLeft: undefined,
  containerProps: (theme) => ({
    borderBottom: '1px solid',
    borderColor: theme.colors.separator.primary,
    pb: '8x',
    pt: '6x',
  }),
  showWhileLoading: false,
  bar: {
    listGap: '2x',
    listMarginTop: '6x',
    listPaddingRight: undefined,
    gap: '1x',
    borderRadius: undefined,
    cornerRadiusRight: '5px',
    trackBackground: (theme) => theme.background.tertiary,
    labelFontWeight: 'regular',
  },
};

const VARIANT_CONFIG: Record<DistributionVariant, VariantConfig> = {
  page: pageConfig,
  gallerySm: galleryBase,
  galleryLg: { ...galleryBase, headingSize: '500', ratingsTextSize: '300' },
  review: {
    ...pageConfig,
    containerProps: () => ({
      borderBottom: '0px',
      pb: '6x',
      pt: '0x',
    }),
  },
};

const RATING_LEVELS = [5, 4, 3, 2, 1] as const;

const ratingLabelSx = { minWidth: '12px', textAlign: 'right' } as const;
const ratingBarTrackSx = { overflow: 'hidden' } as const;

interface RatingBarProps {
  rating: number;
  count: number;
  total: number;
  bar: BarConfig;
}

const RatingBar = ({ rating, count, total, bar }: RatingBarProps): JSX.Element => {
  const theme = useTheme();
  const percentage = total > 0 ? (count / total) * 100 : 0;

  return (
    <Flex
      alignItems="center"
      gap={bar.gap}
      width="100%"
      aria-label={t`${rating} stars: ${count} ratings`}
    >
      <Text
        size="100"
        fontWeight={bar.labelFontWeight}
        textColor="secondary"
        aria-hidden="true"
        sx={ratingLabelSx}
      >
        {rating}
      </Text>

      <Box
        flex="1"
        aria-hidden="true"
        height="12px"
        borderRadius={bar.borderRadius}
        borderTopRightRadius={bar.cornerRadiusRight}
        borderBottomRightRadius={bar.cornerRadiusRight}
        backgroundColor={bar.trackBackground(theme)}
        sx={ratingBarTrackSx}
      >
        <Box
          height="100%"
          width={`${percentage}%`}
          borderRadius={bar.borderRadius}
          borderTopRightRadius={bar.cornerRadiusRight}
          borderBottomRightRadius={bar.cornerRadiusRight}
          backgroundColor={theme.background.attention_highlight}
        />
      </Box>
    </Flex>
  );
};

interface Props {
  isModal?: boolean;
  variant?: DistributionVariant;
}

const RevampDistributionWidget = ({
  isModal = false,
  variant = 'page',
}: Props): JSX.Element | null => {
  const theme = useTheme();
  const { sitterData } = useRevampProfileContext();
  const initialReviewsParams = getInitialReviewsParams();

  const { data: reviews } = useApiPersonDetailReviewsList(sitterData.opk, {
    ...initialReviewsParams,
  });

  const distribution = reviews?.distribution;
  const totalRatings = useMemo(() => {
    return RATING_LEVELS.reduce((accumulator, level) => {
      return accumulator + (distribution?.[String(level)] ?? 0);
    }, 0);
  }, [distribution]);
  const totalTextReviews = reviews?.totalTextReviews ?? 0;

  const config = VARIANT_CONFIG[variant];
  const hasRatings = totalRatings > 0;

  if (!config.showWhileLoading && reviews === undefined) return null;

  const shouldNotAddPaddingBottom = variant === 'review' && !hasRatings && !isModal;
  const containerProps = shouldNotAddPaddingBottom
    ? { ...config.containerProps(theme), pb: '0x' as const }
    : config.containerProps(theme);

  return (
    <Box
      data-testid="revamp-distribution-widget"
      width="100%"
      display="flex"
      flexDirection="column"
      justifyContent={!hasRatings ? 'center' : undefined}
      {...containerProps}
    >
      <Box pl={config.contentPaddingLeft}>
        <Flex alignItems="center" gap="2x">
          <Heading
            size={config.headingSize}
            mb="0x"
            webProps={{ responsive: false }}
            textColor="primary"
          >
            {hasRatings ? sitterData.ratingsAverage : '-'}
          </Heading>
          {hasRatings && <A11yHiddenBox>{t`stars`}</A11yHiddenBox>}
          <ReviewStarFill
            width={config.starIconSize}
            height={config.starIconSize}
            fill={theme.background.attention_highlight}
            aria-hidden="true"
          />
        </Flex>

        <Text size={config.ratingsTextSize} textColor="secondary">
          {plural(totalRatings, { one: '# rating', other: '# ratings' })}
          {isModal && (
            <>
              {' • '}
              {plural(totalTextReviews, { one: '# review', other: '# reviews' })}
            </>
          )}
        </Text>
      </Box>

      {hasRatings && (
        <Flex
          flexDirection="column"
          gap={config.bar.listGap}
          mt={config.bar.listMarginTop}
          pr={config.bar.listPaddingRight}
          display={config.bar.barsDisplay}
        >
          {RATING_LEVELS.map((level) => (
            <RatingBar
              key={`rating-bar-level-${level}`}
              rating={level}
              count={distribution?.[String(level)] ?? 0}
              total={totalRatings}
              bar={config.bar}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};

export default RevampDistributionWidget;
